/**
 * Animated Details Element
 * Adds smooth height transitions to native <details> elements
 */
class DetailsAccordion extends HTMLElement {
  connectedCallback() {
    // Wait for content to be fully parsed
    setTimeout(() => {
      this.details = this.querySelectorAll('details');
      this.initializeDetails();
    }, 0);
  }

  initializeDetails() {
    if (!this.details || this.details.length === 0) {
      return;
    }
    
    this.details.forEach((details, index) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('summary + *');
      const icon = details.querySelector('.accordion-icon');
      
      if (!summary) {
        return;
      }
      
      if (!(content instanceof HTMLElement)) {
        return;
      }
      
      if (!(details instanceof HTMLDetailsElement)) {
        return;
      }

      // Prevent default toggle to control animation
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleDetails(details, content, icon);
      });

      // Set initial state and configure icon
      if (icon instanceof SVGElement || icon instanceof HTMLElement) {
        icon.style.transformOrigin = 'center';
        icon.style.transition = 'transform 0.3s ease';
      }
      
      if (!details.open) {
        content.style.height = '0';
        content.style.overflow = 'hidden';
        if (icon instanceof SVGElement || icon instanceof HTMLElement) {
          icon.style.transform = 'rotate(0deg)';
        }
      } else {
        // If already open, rotate icon
        if (icon instanceof SVGElement || icon instanceof HTMLElement) {
          icon.style.transform = 'rotate(-180deg)';
        }
      }
      
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} content
   * @param {Element | null} icon
   */
  toggleDetails(details, content, icon = null) {
    const isOpen = details.open;

    if (isOpen) {
      // Closing - animate from current height to 0
      const startHeight = content.scrollHeight;
      content.style.height = startHeight + 'px';
      content.style.overflow = 'hidden';
      
      // Force reflow
      requestAnimationFrame(() => {
        content.style.transition = 'height 0.3s ease';
        content.style.height = '0px';
        content.style.padding = '0';
        
        // Rotate icon back
        if (icon instanceof SVGElement || icon instanceof HTMLElement) {
          icon.style.transform = 'rotate(0deg)';
        }
      });
      
      content.addEventListener('transitionend', () => {
          content.style.transition = '';
          content.style.overflow = '';
          details.removeAttribute('open');
      }, { once: true });
      
    } else {
      // Opening - set details open first, then animate height
      details.setAttribute('open', '');
      
      const endHeight = content.scrollHeight;
      content.style.height = '0px';
      content.style.overflow = 'hidden';
      
      // Use requestAnimationFrame to ensure smooth animation
      requestAnimationFrame(() => {
        content.style.transition = 'height 0.3s ease';
        content.style.height = endHeight + 'px';
        
        // Rotate icon
        if (icon instanceof SVGElement || icon instanceof HTMLElement) {
          icon.style.transform = 'rotate(-180deg)';
        }
      });
      
      content.addEventListener('transitionend', () => {
        content.style.height = '';
        content.style.overflow = '';
        content.style.transition = '';
      }, { once: true });
    }
  }
}

if (!customElements.get('details-accordion')) {
  customElements.define('details-accordion', DetailsAccordion);
}
